// @ts-check
import { Eye, RefreshCw, Save } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import MarkdownPreview from '../components/ui/MarkdownPreview';
import PreviewModal from '../components/ui/PreviewModal';
import { useToast } from '../context/ToastContext';
import { useRegisterUnsavedChanges } from '../context/UnsavedChangesContext';
import api from '../utils/apiClient';

// Mirrors frontend-next's CmsContentPage.tsx `processContent` — resolves the same
// {{RATES_TABLE}} / {threshold} placeholders so the preview matches what customers
// will actually see instead of showing raw template syntax.
const buildRatesTable = (shippingConfig) => {
  if (!shippingConfig) return '_(shipping rates unavailable for preview)_';
  const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
  const rows = ['| Destination | Shipping Rate |', '|-------------|---------------|'];
  for (const zone of shippingConfig.zones || []) {
    rows.push(`| ${zone.name} | ${zone.rate_inr === 0 ? 'Free' : inr(zone.rate_inr)} |`);
  }
  rows.push(`| All other destinations | ${inr(shippingConfig.default_rate_inr)} |`);
  rows.push('');
  const threshold = shippingConfig.free_threshold_inr;
  rows.push(
    threshold ? `*Complimentary shipping on orders above ${inr(threshold)}*` : '*Complimentary shipping on all orders*',
  );
  return rows.join('\n');
};

const processContentForPreview = (raw, shippingConfig) => {
  const threshold = shippingConfig
    ? `₹${Number(shippingConfig.free_threshold_inr || 0).toLocaleString('en-IN')}`
    : '{threshold}';
  return (raw || '')
    .replace(/\{\{RATES_TABLE\}\}/g, buildRatesTable(shippingConfig))
    .replace(
      /on orders above\s*\{threshold\}/gi,
      shippingConfig?.free_threshold_inr ? `on orders above ${threshold}` : 'on all orders',
    )
    .replace(/\{threshold\}/g, threshold)
    .replace(/\{\{[^}]+\}\}/g, '');
};

const SiteContentPage = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [contents, setContents] = useState([]);
  const [activeTab, setActiveTab] = useState('privacy_policy');
  const [showPreview, setShowPreview] = useState(false);
  const [shippingConfig, setShippingConfig] = useState(null);

  const contentTypes = useMemo(
    () => [
      { key: 'privacy_policy', label: 'Privacy Policy' },
      { key: 'terms_conditions', label: 'Terms & Conditions' },
      { key: 'shipping_policy', label: 'Shipping Policy' },
      { key: 'return_policy', label: 'Return Policy' },
      { key: 'faq', label: 'FAQs' },
      { key: 'customs_duties_taxes', label: 'Customs, Duties & Taxes' },
      { key: 'cookie_consent', label: 'Cookie Consent Banner' },
    ],
    [],
  );

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    meta_description: '',
    is_published: true,
  });
  // Tracks "touched since load/tab-switch" — not a deep diff — reset by the
  // load/tab-switch effect below and after a successful save.
  const [isDirty, setIsDirty] = useState(false);
  useRegisterUnsavedChanges(isDirty);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const loadContents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get('/api/admin/site-content');
      setContents(data.items);
    } catch (error) {
      console.error('Failed to load content:', error);
      setError('Failed to load site content. Please try again.');
      toast.error('Failed to load site content');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadContents();
  }, [loadContents]);

  // Loaded once for the preview's {{RATES_TABLE}} / {threshold} substitution — failure just
  // means the preview shows a placeholder note instead of resolved rates, never blocks editing.
  useEffect(() => {
    api
      .get('/api/site-settings/')
      .then((allSettings) => {
        const setting = allSettings.find((s) => s.key === 'shipping_config');
        if (setting?.value) setShippingConfig(JSON.parse(setting.value));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const activeContent = contents.find((c) => c.page_type === activeTab);
    if (activeContent) {
      setFormData({
        title: activeContent.title || '',
        content: activeContent.content || '',
        meta_description: activeContent.meta_description || '',
        is_published: activeContent.is_published ?? true,
      });
    } else {
      // Reset for new content
      const defaultTitle = contentTypes.find((t) => t.key === activeTab)?.label || '';
      setFormData({
        title: defaultTitle,
        content: '',
        meta_description: '',
        is_published: true,
      });
    }
    setShowPreview(false);
    setIsDirty(false);
  }, [activeTab, contents, contentTypes]);

  const changeTab = (nextTab) => {
    if (nextTab === activeTab) return;
    if (isDirty && !window.confirm('You have unsaved changes on this page. Switch tabs anyway?')) {
      return;
    }
    setActiveTab(nextTab);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const activeContent = contents.find((c) => c.page_type === activeTab);

      const payload = activeContent ? formData : { ...formData, page_type: activeTab };

      if (activeContent) {
        await api.patch(`/api/admin/site-content/${activeTab}`, payload);
      } else {
        await api.post('/api/admin/site-content', payload);
      }

      toast.success('Content saved successfully');
      setShowPreview(false);
      await loadContents();
    } catch (error) {
      console.error('Failed to save content:', error);
      toast.error(error.message || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  // "Save Changes" always routes through the preview first — the actual write only
  // happens when the admin confirms from there, so nothing publishes unseen. The
  // standalone "Preview" button opens the same modal, just without forcing a save.
  const openPreview = () => setShowPreview(true);

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        <button
          onClick={loadContents}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <RefreshCw size={18} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-serif">Site Content</h1>
        <button
          onClick={loadContents}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg border-input hover:bg-muted/50 disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <div className="flex gap-4 overflow-x-auto">
          {contentTypes.map((type) => (
            <button
              key={type.key}
              onClick={() => changeTab(type.key)}
              className={`flex-shrink-0 whitespace-nowrap px-4 py-3 border-b-2 transition ${
                activeTab === type.key
                  ? 'border-foreground font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* Editor */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">Page Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => updateField('title', e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
              placeholder="Enter page title"
            />
          </div>

          {/* Meta Description */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Meta Description
              <span className="text-xs text-muted-foreground ml-2">(For SEO)</span>
            </label>
            <input
              type="text"
              value={formData.meta_description}
              onChange={(e) => updateField('meta_description', e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
              placeholder="Brief description for search engines"
              maxLength={300}
            />
            <p className="text-xs text-muted-foreground mt-1">{formData.meta_description.length}/300 characters</p>
          </div>

          {/* Content (Markdown) */}
          <div>
            <label className="block text-sm font-medium mb-2">Content (Markdown Supported)</label>
            <textarea
              value={formData.content}
              onChange={(e) => updateField('content', e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground font-mono text-sm"
              rows={20}
              placeholder="Enter content using Markdown formatting...

## Example Heading
**Bold text**, *italic text*

- List item 1
- List item 2

[Link text](https://example.com)"
            />
            <p className="text-xs text-muted-foreground mt-2">
              <strong>Markdown Quick Guide:</strong> **bold**, *italic*, ## Heading, - List, [Link](url)
            </p>
          </div>

          {/* Published Status */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_published"
              checked={formData.is_published}
              onChange={(e) => updateField('is_published', e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="is_published" className="text-sm font-medium">
              Published (visible to customers)
            </label>
          </div>

          {/* Preview + Save */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={openPreview}
              className="flex items-center gap-2 px-6 py-2 border rounded-lg border-input hover:bg-muted/50"
            >
              <Eye size={18} />
              Preview
            </button>
            <button
              onClick={openPreview}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {showPreview && (
        <PreviewModal
          title={`Preview: ${contentTypes.find((t) => t.key === activeTab)?.label || activeTab}`}
          subtitle={formData.meta_description || undefined}
          onClose={() => setShowPreview(false)}
          onConfirmSave={handleSave}
          saving={saving}
        >
          <MarkdownPreview
            title={formData.title}
            content={processContentForPreview(formData.content, shippingConfig)}
          />
        </PreviewModal>
      )}
    </div>
  );
};

export default SiteContentPage;
